import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { EmptyState } from '../../components/feedback/EmptyState'
import { usePmocDuplicar, usePmocList } from '../../hooks/usePmoc'
import styles from '../_shared/CrudPage.module.css'

const statusCores: Record<string, { bg: string; color: string }> = {
  rascunho: { bg: '#FFF3E0', color: '#F57C00' },
  ativo: { bg: '#E8F5E9', color: '#388E3C' },
  expirado: { bg: '#F5F5F5', color: '#757575' },
  revogado: { bg: '#F5F5F5', color: '#757575' },
  bloqueado: { bg: '#FFEBEE', color: '#C62828' },
}

export default function PmocPage() {
  const [status, setStatus] = useState('')
  const navigate = useNavigate()
  const { data, isLoading } = usePmocList({ status: status || undefined })
  const duplicar = usePmocDuplicar()

  async function handleDuplicar(id: string) {
    try {
      const novo = await duplicar.mutateAsync(id)
      notifications.show({
        color: 'green',
        message: `PMOC v${novo.versao} criado como rascunho.`,
      })
      navigate(`/pmoc/${novo.id}`)
    } catch {
      notifications.show({ color: 'red', message: 'Falha ao duplicar PMOC.' })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>PMOCs</h1>
        <Link to="/pmoc/novo" className={styles.btnNovo ?? ''} data-testid="btn-pmoc-novo">
          + Novo PMOC
        </Link>
      </header>
      <div className={styles.toolbar}>
        <select
          className={styles.search}
          style={{ maxWidth: 200 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="pmoc-filtro-status"
          aria-label="Filtrar PMOCs por status"
        >
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="ativo">Ativo</option>
          <option value="expirado">Expirado</option>
          <option value="revogado">Revogado</option>
          <option value="bloqueado">Bloqueado</option>
        </select>
      </div>
      {isLoading ? (
        <div className={styles.loading}>Carregando PMOCs...</div>
      ) : (
        <div className={styles.grid}>
          {data?.results.map((pmoc) => {
            const cor = statusCores[pmoc.status] ?? { bg: '#F5F5F5', color: '#757575' }
            return (
              <div key={pmoc.id} className={styles.card}>
                <Link
                  to={`/pmoc/${pmoc.id}`}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <div className={styles.cardHeader}>
                    <span
                      style={{
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: 'var(--text-secondary)',
                        fontWeight: 600,
                      }}
                    >
                      PMOC v{pmoc.versao}
                    </span>
                    <span
                      className={styles.badge}
                      style={{ background: cor.bg, color: cor.color }}
                    >
                      {pmoc.status}
                    </span>
                  </div>
                  <h3 className={styles.cardTitle}>
                    {pmoc.titulo || pmoc.cliente_nome || pmoc.site_nome}
                  </h3>
                  {(pmoc.cliente_nome || pmoc.site_nome) && (
                    <p className={styles.cardSub}>📍 {pmoc.cliente_nome ?? pmoc.site_nome}</p>
                  )}
                  <div className={styles.cardFooter}>
                    <span className={styles.cardSub}>
                      Vigência: {pmoc.vigencia_inicio ?? '—'}
                      {pmoc.vigencia_fim ? ` → ${pmoc.vigencia_fim}` : ''}
                    </span>
                  </div>
                </Link>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={() => handleDuplicar(pmoc.id)}
                    disabled={duplicar.isPending}
                    style={{
                      flex: 1,
                      padding: '0.4rem',
                      background: 'white',
                      color: '#1565C0',
                      border: '1px solid #1565C0',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                    }}
                  >
                    ⎘ Duplicar
                  </button>
                </div>
              </div>
            )
          })}
          {data?.results.length === 0 && (
            <EmptyState
              icon="📋"
              title={status ? `Nenhum PMOC com status "${status}"` : 'Nenhum PMOC criado ainda'}
              description={
                status
                  ? 'Tente outro filtro ou limpe pra ver todos.'
                  : 'O PMOC é o documento central. Aplica planos NBR 17.037 a um site e gera ordens de serviço automaticamente.'
              }
              actionTo={status ? undefined : '/pmoc/novo'}
              actionLabel={status ? undefined : '+ Novo PMOC'}
            />
          )}
        </div>
      )}
    </div>
  )
}
